import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import authService from '../services/authService';

/**
 * Authentication Context for managing user authentication state.
 * 
 * This context provides authentication functionality throughout the app,
 * including login, logout, registration, and token management.
 * 
 * @author BagABoard Team
 */

const AuthContext = createContext();

/**
 * Custom hook to use the authentication context.
 * Throws an error if used outside of AuthProvider.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Authentication Provider component.
 * Wraps the app and provides authentication state and methods.
 */
export function AuthProvider({ children }) {
  // Authentication state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  /**
   * Initialize authentication state on app load.
   * Checks for existing token and validates it.
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Initialize authentication by checking stored token.
   */
  const initializeAuth = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        // Validate token with backend
        const isValid = await authService.validateToken(storedToken);
        if (isValid) {
          // Token is valid, get user info
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setToken(storedToken);
        } else {
          // Token is invalid, remove it
          localStorage.removeItem('token');
          setToken(null);
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Clear invalid token
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login user with email and password.
   * 
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<boolean>} - Success status
   */
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authService.login(email, password);
      
      if (response.token) {
        // Store token and user data
        localStorage.setItem('token', response.token);
        setToken(response.token);
        setUser({
          userId: response.userId,
          email: response.email,
          fullName: response.fullName,
          role: response.role
        });
        
        // Don't show toast here - let the calling component handle it
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      // Don't show toast here - let the calling component handle it
      throw error; // Re-throw so Login.js can catch and handle it
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user account.
   * 
   * @param {Object} userData - User registration data
   * @returns {Promise<boolean>} - Success status
   */
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authService.register(userData);
      
      if (response.token) {
        // Store token and user data
        localStorage.setItem('token', response.token);
        setToken(response.token);
        setUser({
          userId: response.userId,
          email: response.email,
          fullName: response.fullName,
          role: response.role
        });
        
        toast.success(`Welcome to BagAboard, ${response.fullName}!`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout current user.
   * Clears token and user data from state and localStorage.
   */
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.info('You have been logged out.');
  };

  /**
   * Refresh authentication token.
   * 
   * @returns {Promise<boolean>} - Success status
   */
  const refreshToken = async () => {
    try {
      if (!token) return false;
      
      const response = await authService.refreshToken(token);
      if (response.token) {
        localStorage.setItem('token', response.token);
        setToken(response.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      logout();
      return false;
    }
  };

  /**
   * Check if user is authenticated.
   * 
   * @returns {boolean} - Authentication status
   */
  const isAuthenticated = () => {
    return !!(token && user);
  };

  /**
   * Check if user has admin role.
   * 
   * @returns {boolean} - Admin status
   */
  const isAdmin = () => {
    return user?.role === 'ADMIN';
  };

  /**
   * Get current user data.
   * 
   * @returns {Object|null} - User data or null
   */
  const getCurrentUser = () => {
    return user;
  };

  /**
   * Get current authentication token.
   * 
   * @returns {string|null} - Token or null
   */
  const getToken = () => {
    return token;
  };

  // Context value object
  const value = {
    // State
    user,
    loading,
    token,
    
    // Methods
    login,
    register,
    logout,
    refreshToken,
    isAuthenticated,
    isAdmin,
    getCurrentUser,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
